// Manticore — CDN / Edge service API. Shared infra used across the feature
// modules in this folder. Ported from the legacy @console
// `External.SwiftFederation` connectors (renamed Manticore). All endpoints live
// under SERVICE_API_V3 `/cdn/*` and are path-scoped by domain (no service_uuid).
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';

const serviceApiV3 = getHttpClient('serviceApiV3');

// ---- Response envelope ---------------------------------------------------
// CDN endpoints return the unified `{ data: T, meta?, pagination? }` shape.
export interface DomainListPagination {
  page: number;
  per_page: number;
  total: number;
}
export interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
  pagination?: DomainListPagination;
  message?: string;
}

// ---- Query keys ----------------------------------------------------------
export const manticoreKeys = {
  all: ['cdn'] as const,
  domainsPaged: (q: { page: number; perPage: number; search?: string; sort?: string; order?: string; projectId?: string; teamId?: string; tags?: string[]; statuses?: string[]; createdFrom?: string; createdTo?: string }) =>
    ['cdn', 'domains', 'paged', q.page, q.perPage, q.search ?? '', q.sort ?? '', q.order ?? '', q.projectId ?? '', q.teamId ?? '', (q.tags ?? []).join(','), (q.statuses ?? []).join(','), q.createdFrom ?? '', q.createdTo ?? ''] as const,
  domainTags: ['cdn', 'domains', 'tags'] as const,
  domainsAll: ['cdn', 'domains', 'all'] as const,
  domain: (domain: string) => ['cdn', 'domains', domain] as const,
  cache: (domain: string) => ['cdn', 'domains', domain, 'cache'] as const,
  tls: (domain: string) => ['cdn', 'domains', domain, 'tls'] as const,
  certs: (domain: string) => ['cdn', 'domains', domain, 'certs'] as const,
  certsGlobal: ['cdn', 'certs'] as const,
  upstream: (domain: string) => ['cdn', 'domains', domain, 'upstream'] as const,
  originRewrite: (domain: string) => ['cdn', 'domains', domain, 'origin-rewrite'] as const,
  headerRules: (domain: string) => ['cdn', 'domains', domain, 'header-rules'] as const,
  errorPages: (domain: string) => ['cdn', 'domains', domain, 'error-pages'] as const,
  redirects: (domain: string) => ['cdn', 'domains', domain, 'redirects'] as const,
  cachePolicies: (domain: string) => ['cdn', 'domains', domain, 'cache-policies'] as const,
};

// ---- Vendor-unwrap helpers ----------------------------------------------
/** Unwrap a vendor fanout map `{ raven: [...] }` → first array, else passthrough. */
export function unwrapFanoutArray<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const first = Object.values(raw as object)[0];
    if (Array.isArray(first)) return first as T[];
  }
  return [];
}

/** Unwrap a single vendor-keyed object `{ raven: {...} }` → `{...}` when the
 *  config has no recognizable top-level key. */
export function unwrapVendorObject(raw: unknown, markers: string[]): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as Record<string, unknown>;
  const looksFlat = markers.some((m) => m in obj);
  if (!looksFlat) {
    const first = Object.values(obj)[0];
    if (first && typeof first === 'object') return first as Record<string, unknown>;
  }
  return obj;
}

/** First value of a vendor-keyed object, else fallback. */
export function firstVendor<T>(data: unknown, fallback: T): T {
  if (data && typeof data === 'object') {
    const first = Object.values(data as object)[0];
    if (first !== undefined) return first as T;
  }
  return fallback;
}

// ---- Core-plane read envelope ---------------------------------------------
// Aspect GETs return `{ core, core_provider, vendors }`: `core` is the
// canonical (vEdge) binding's config — the single source of truth — and
// `vendors` reports every binding's read outcome (ok | unsupported | error),
// including failures the old bare fan-out map dropped silently.
export interface VendorReadState {
  status: 'ok' | 'unsupported' | 'error';
  error?: string;
  /** The vendor's own config when status is ok (drift inspection). */
  config?: unknown;
}

export interface CoreRead<T> {
  core: T | null;
  coreProvider: string;
  vendors: Record<string, VendorReadState>;
}

/** Parse a core-read envelope; falls back to the legacy bare vendor map
 *  (first vendor wins, statuses assumed ok) so the FE survives an older BE. */
export function parseCoreRead<T>(raw: unknown): CoreRead<T> {
  if (raw && typeof raw === 'object' && 'vendors' in raw && 'core_provider' in raw) {
    const env = raw as { core?: T | null; core_provider?: string; vendors?: Record<string, VendorReadState> };
    return { core: env.core ?? null, coreProvider: env.core_provider ?? '', vendors: env.vendors ?? {} };
  }
  const vendors: Record<string, VendorReadState> = {};
  if (raw && typeof raw === 'object') {
    for (const k of Object.keys(raw)) vendors[k] = { status: 'ok' };
  }
  return { core: firstVendor<T | null>(raw, null), coreProvider: '', vendors };
}

// ============================================
// Generic CDN config resource (GET/PUT/DELETE /cdn/domains/:d/<path>)
// ----------------------------------------------------------------------------
// Most Security/Optimization endpoints share the same envelope: GET returns the
// config object (or {} on 400/404), PUT upserts it, DELETE clears it. One
// factory drives security-headers, cors, bot, bot-mgmt, rate-limit,
// access-control, compression, image-optimization, timeouts, and WAF master.
// ============================================
const cdnConfigKey = (domain: string, path: string) => ['cdn', 'domains', domain, path] as const;

// The full aspect read kept in the query cache: the raw fan-out payload plus the
// parsed per-vendor outcomes. Config extraction happens per-observer in `select`
// (marker-aware) so `useCdnConfig` and `useCdnAspectVendors` can share one fetch
// without their differing options corrupting the cached result.
interface CdnAspectRead {
  raw: unknown;
  /** Parsed core (vEdge) config — null when the aspect is unconfigured. */
  core: unknown;
  vendors: Record<string, VendorReadState>;
  coreProvider: string;
}

/** Extract the core config object from a raw aspect payload (see useCdnConfig). */
function extractCdnConfig<T>(raw: unknown, vendorMarkers?: string[]): T | null {
  // Config existence is signalled by the vendor fan-out: an un-configured
  // resource returns `{ data: {} }` (no vendor key), whereas a configured
  // one returns `{ data: { "<vendor>": {...} } }` — even when the inner
  // config is "empty" (e.g. `{ rules: [] }`). So presence of ANY vendor
  // key = configured; `{}` = not configured. Don't infer this from the
  // inner content, which can legitimately be empty after a create.
  if (!raw || typeof raw !== 'object' || Object.keys(raw as object).length === 0) return null;
  const cfg = vendorMarkers ? unwrapVendorObject(raw, vendorMarkers) : (raw as Record<string, unknown>);
  return cfg as T;
}

async function fetchCdnAspect(domain: string | undefined, path: string): Promise<CdnAspectRead> {
  try {
    const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/${path}`);
    const raw = res.data?.data ?? null;
    const { core, vendors, coreProvider } = parseCoreRead<unknown>(raw);
    return { raw, core, vendors, coreProvider };
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 400 || status === 404) return { raw: null, core: null, vendors: {}, coreProvider: '' };
    throw e;
  }
}

export function useCdnConfig<T extends Record<string, unknown> = Record<string, unknown>>(
  domain: string | undefined,
  path: string,
  opts?: { vendorMarkers?: string[] },
) {
  return useQuery({
    queryKey: cdnConfigKey(domain ?? '', path),
    enabled: !!domain,
    retry: false,
    queryFn: () => fetchCdnAspect(domain, path),
    select: (r: CdnAspectRead) => extractCdnConfig<T>(r.raw, opts?.vendorMarkers),
  });
}

/**
 * The per-vendor read outcomes for a domain aspect, used to surface
 * secondary-provider drift: the core (vEdge) binding may save fine while a
 * best-effort secondary fails. Fetches under its OWN cache key (…, path,
 * '__vendors') so it can sit next to ANY aspect read hook — including the
 * dedicated cache/redirects/etc hooks that transform the payload to a different
 * shape — without corrupting their cached result.
 */
export function useCdnAspectVendors(domain: string | undefined, path: string) {
  return useQuery({
    queryKey: [...cdnConfigKey(domain ?? '', path), '__vendors'] as const,
    enabled: !!domain,
    retry: false,
    queryFn: () => fetchCdnAspect(domain, path),
    select: (r: CdnAspectRead) => ({
      vendors: r.vendors,
      coreProvider: r.coreProvider,
      // Only meaningful to talk about "drift" when the core has a config the
      // secondaries were supposed to mirror. An unconfigured aspect whose
      // secondary reads error is not drift — it's just nothing set.
      coreConfigured: r.core != null,
    }),
  });
}

export function useUpdateCdnConfig(
  domain: string,
  path: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: Record<string, unknown>) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/${path}`, config);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: cdnConfigKey(domain, path) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteCdnConfig(
  domain: string,
  path: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/${path}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: cdnConfigKey(domain, path) });
      options?.onSuccess?.(...args);
    },
  });
}
